import type {
  SteelOrderCategoryEnum,
  SteelOrderDetailFieldsFragment,
  SteelOrderFieldsFragment,
  SteelOrderMatchingStatusEnum,
  SteelOrderQualityCheckStatusEnum,
} from '@mbfab/client-api'
import type { ShapeNameConversionDisplayMap } from '@mbfab/shared'
import { convertShapeNameForDisplay, isSome } from '@mbfab/shared'
import dayjs from 'dayjs'
import { sum, uniq } from 'es-toolkit'

export type SteelOrderItemViewModel = {
  id: string
  // 管理番号・工事番号・工事名称・建築備考は、一覧の明細展開行にも明細ごとの値を出すため
  // 基底型に持たせる（20260907_01 追加実装。親行側の集約列 managementNumbers 等とは別に、
  // 明細1件ごとの値をここで持つ）
  managementNumber: string | null
  constructionNumber: string | null
  constructionName: string | null
  productName: string | null
  standard: string | null
  size: string | null
  length: string | null
  quantity: number | null
  weight: number | null
  manufacturerName: string | null
  itemNumber: string | null
  answeredDeliveryDate: string | null
  constructionNote: string | null
  // 品管照合ステータスは一覧の明細展開行・発注詳細の両方で表示・編集するため基底型に持たせる
  // （20260907_01: 品管照合ステータスの3値化・一覧展開行への列追加）。rank は
  // QualityCheckStatusCell の testid（参照番号 + rank で一意）に使う
  qualityCheckStatus: SteelOrderQualityCheckStatusEnum
  qualityCheckUpdatedAt: string | null
  rank: number
}

// 紐付きミルシートの PDF。発注詳細のステータスパネルが「PDFあり」の枚数を数えるためだけに持つ
// （原本は署名URLを取らず、ミルシート詳細ページのプレビューで見せる）
export type SteelOrderMillsheetPdfViewModel = {
  id: string
}

// 発注一覧の「ミルシート」列から詳細ページへリンクするための最小項目
export type SteelOrderMillsheetLinkViewModel = {
  id: string
  no: string
}

export type SteelOrderViewModel = {
  id: string
  tenantId: string
  referenceNumber: string
  category: SteelOrderCategoryEnum
  isMillsheetExcluded: boolean
  millsheetStatus: SteelOrderMatchingStatusEnum
  orderStatus: SteelOrderMatchingStatusEnum
  qualityCheckStatus: SteelOrderQualityCheckStatusEnum
  // ミルシート重量の不足量（あと何kg）。対象外、または不足が無い場合は null
  // （v1 SteelOrdersTable の算出ロジックを踏襲）
  millsheetShortfallKg: number | null
  outputDate: string | null
  supplierName: string | null
  totalWeight: number
  orderedWeight: number
  millsheetWeight: number
  importedAt: string
  // 明細の集約列（v1 uniqueItemValues 相当。重複を1つに、空欄は除いてカンマ区切り）
  managementNumbers: string
  constructionNumbers: string
  constructionNames: string
  productNames: string
  standards: string
  manufacturerNames: string
  items: SteelOrderItemViewModel[]
  millsheets: SteelOrderMillsheetLinkViewModel[]
}

// 発注詳細のサブタイトル（identity-subtitle.tsx）でも同じ「重複除去 + 空欄除去」が要るため export する
export function uniqueItemValues<T>(items: T[], pick: (item: T) => string | null | undefined): string[] {
  return uniq(items.map(pick).filter(isSome)).filter((value) => value !== '')
}

function aggregateItemValues(
  items: SteelOrderFieldsFragment['items'],
  pick: (item: SteelOrderFieldsFragment['items'][number]) => string | null | undefined,
): string {
  return uniqueItemValues(items, pick).join(', ')
}

export function toSteelOrderViewModel(fragment: SteelOrderFieldsFragment): SteelOrderViewModel {
  const shortage = fragment.totalWeight - fragment.millsheetWeight
  const millsheetShortfallKg = !fragment.isMillsheetExcluded && shortage > 0 ? shortage : null

  return {
    id: fragment.id,
    tenantId: fragment.tenantId,
    referenceNumber: fragment.referenceNumber,
    category: fragment.category,
    isMillsheetExcluded: fragment.isMillsheetExcluded,
    millsheetStatus: fragment.millsheetStatus,
    orderStatus: fragment.orderStatus,
    qualityCheckStatus: fragment.qualityCheckStatus,
    millsheetShortfallKg,
    outputDate: fragment.outputDate,
    supplierName: fragment.supplierName,
    totalWeight: fragment.totalWeight,
    orderedWeight: fragment.orderedWeight,
    millsheetWeight: fragment.millsheetWeight,
    importedAt: fragment.importedAt,
    managementNumbers: aggregateItemValues(fragment.items, (item) => item.managementNumber),
    constructionNumbers: aggregateItemValues(fragment.items, (item) => item.constructionNumber),
    constructionNames: aggregateItemValues(fragment.items, (item) => item.constructionName),
    productNames: aggregateItemValues(fragment.items, (item) => item.productName),
    standards: aggregateItemValues(fragment.items, (item) => item.standard),
    manufacturerNames: aggregateItemValues(fragment.items, (item) => item.manufacturerName),
    items: fragment.items.map((item) => ({
      id: item.id,
      managementNumber: item.managementNumber,
      constructionNumber: item.constructionNumber,
      constructionName: item.constructionName,
      productName: item.productName,
      standard: item.standard,
      size: item.size,
      length: item.length,
      quantity: item.quantity,
      weight: item.weight,
      manufacturerName: item.manufacturerName,
      itemNumber: item.itemNumber,
      answeredDeliveryDate: item.answeredDeliveryDate,
      constructionNote: item.constructionNote,
      qualityCheckStatus: item.qualityCheckStatus,
      qualityCheckUpdatedAt: item.qualityCheckUpdatedAt,
      rank: item.rank,
    })),
    millsheets: fragment.millsheets.map((millsheet) => ({ id: millsheet.id, no: millsheet.no })),
  }
}

// 発注詳細画面用の明細（一覧の展開行が使う SteelOrderItemViewModel に、詳細画面だけで使う項目を足したもの）。
// managementNumber/constructionNumber/constructionName/constructionNote/qualityCheckStatus/
// qualityCheckUpdatedAt/rank は基底型（SteelOrderItemViewModel）が持つためここでは重ねない。
// createdAt（発注データ登録日時）は一覧の明細展開行には出さない（親行と同時刻のため不要。
// 20260907_01 ユーザー判断）詳細画面だけの項目
export type SteelOrderDetailItemViewModel = SteelOrderItemViewModel & {
  createdAt: string
}

// 紐付きミルシートの明細（v1 SteelOrderMillsheetsTable の展開行と同じ項目）。
// size / length は変換マスタ適用後の表示値を持たせる（照合には使わないため view model の時点で変換して確定する）
export type SteelOrderDetailMillsheetItemViewModel = {
  id: string
  size: string | null
  length: string | null
  quantity: number
  weightBeforeProcessing: number | null
  heatNumber: string
}

export type SteelOrderDetailMillsheetViewModel = {
  id: string
  tenantId: string
  no: string
  orderNumber: string
  constructionName: string
  manufacturerName: string
  issuedDate: string
  // 品名・規格は明細（millsheetItems）からの集約表示（v1 SteelOrderMillsheetsTable の sectionTypes/steelTypes 列と同じ）
  productNames: string
  steelTypes: string
  totalWeight: number
  pdfs: SteelOrderMillsheetPdfViewModel[]
  items: SteelOrderDetailMillsheetItemViewModel[]
}

export type SteelOrderDetailViewModel = Omit<SteelOrderViewModel, 'items' | 'millsheets'> & {
  items: SteelOrderDetailItemViewModel[]
  millsheets: SteelOrderDetailMillsheetViewModel[]
  // ステータスパネル（status-panel.tsx）の根拠数値。v1 SteelOrderStatusPanel は steelOrder から都度算出するが、
  // v2 は view model 変換をコンテナ側に寄せる方針（PR A の toSteelOrderViewModel と同じ）に合わせてここで確定する
  checkedItemCount: number
  millsheetPdfCount: number
}

export function toSteelOrderDetailViewModel(
  fragment: SteelOrderDetailFieldsFragment,
  shapeNameConversionMap: ShapeNameConversionDisplayMap,
): SteelOrderDetailViewModel {
  const base = toSteelOrderViewModel(fragment)

  const items: SteelOrderDetailItemViewModel[] = fragment.items.map((item) => ({
    id: item.id,
    managementNumber: item.managementNumber,
    constructionNumber: item.constructionNumber,
    productName: item.productName,
    standard: item.standard,
    size: item.size,
    length: item.length,
    quantity: item.quantity,
    weight: item.weight,
    manufacturerName: item.manufacturerName,
    itemNumber: item.itemNumber,
    answeredDeliveryDate: item.answeredDeliveryDate,
    constructionName: item.constructionName,
    constructionNote: item.constructionNote,
    qualityCheckStatus: item.qualityCheckStatus,
    qualityCheckUpdatedAt: item.qualityCheckUpdatedAt,
    rank: item.rank,
    createdAt: item.createdAt,
  }))

  const millsheets: SteelOrderDetailMillsheetViewModel[] = fragment.millsheets.map((millsheet) => ({
    id: millsheet.id,
    tenantId: millsheet.tenantId,
    no: millsheet.no,
    orderNumber: millsheet.orderNumber,
    constructionName: millsheet.constructionName,
    manufacturerName: millsheet.manufacturerName,
    // importedAt と同じ表記（YYYY/MM/DD）に揃える（v1 と同じ。orders-table-columns.tsx の importedAt 表示を参照）
    issuedDate: dayjs(millsheet.issuedDate).format('YYYY/MM/DD'),
    productNames: uniq(
      millsheet.millsheetItems.map(
        (item) =>
          convertShapeNameForDisplay(shapeNameConversionMap, 'productName', item.sectionType) ?? item.sectionType,
      ),
    ).join(', '),
    steelTypes: uniq(millsheet.millsheetItems.map((item) => item.steelType)).join(', '),
    totalWeight: sum(millsheet.millsheetItems.map((item) => item.weightBeforeProcessing).filter(isSome)),
    pdfs: millsheet.s3Objects
      .filter((s3Object) => s3Object.resourceType === 'millsheets')
      .map((s3Object) => ({ id: s3Object.id })),
    items: millsheet.millsheetItems.map((item) => ({
      id: item.id,
      size: convertShapeNameForDisplay(shapeNameConversionMap, 'size', item.size),
      length: convertShapeNameForDisplay(shapeNameConversionMap, 'length', item.length),
      quantity: item.quantity,
      weightBeforeProcessing: item.weightBeforeProcessing,
      heatNumber: item.heatNumber,
    })),
  }))

  return {
    ...base,
    items,
    millsheets,
    checkedItemCount: items.filter((item) => item.qualityCheckStatus === 'completed').length,
    // v1 と同じく「PDFが1枚でも紐付いているミルシートの枚数」（ミルシート単位のカウントで、PDF枚数の合計ではない）
    millsheetPdfCount: millsheets.filter((millsheet) => millsheet.pdfs.length > 0).length,
  }
}
